/**
 * Service user-side de badges — usado pela tela `/perfil?tab=progress`
 * (BadgesCard). Separado do `admin/badges.service.ts` porque consome
 * endpoints diferentes e devolve formas diferentes (UserBadge vs Badge).
 *
 * Endpoints consumidos:
 *   GET /users/me/badges   — useMyBadges      (badges conquistadas)
 *   GET /badges            — useVisibleBadges (catálogo visível: global + minha empresa)
 *
 * O cruzamento "conquistada vs bloqueada" fica no componente — aqui
 * só servimos os dois conjuntos. Mantém o service "burro" e permite
 * usar `useMyBadges` isoladamente em outras telas (ex.: contador no
 * header).
 */

import { useQuery } from '@tanstack/react-query'
import api from './api'
import type { Badge } from '@/components/super-admin/badges/types'

/** Espelha o back: linha de user_badges com badge embutida via JOIN. */
export interface UserBadge {
  badge: Badge
  /** ISO string serializado pelo Fastify a partir do `Date` do Prisma. */
  earnedAt: string
  /** 'global' (recurrence=once) / '2026' / '2026-W18' / '2026-12'. */
  cycleKey: string
  /** Como veio: evaluator (automatic), super_admin (manual), script (backfill). */
  source: 'automatic' | 'manual' | 'backfill'
}

const MY_BADGES_KEY = ['my-badges'] as const
const VISIBLE_BADGES_KEY = ['visible-badges'] as const

/**
 * Intervalo de polling do `useMyBadges`. O evaluator do back concede
 * badges de forma assíncrona (worker a cada ~2s) — sem WebSocket, o
 * front precisa re-buscar pra detectar conquistas novas e disparar a
 * notificação (`useBadgeUnlockDetector`). 20s equilibra "ao vivo" com
 * carga na API.
 */
const MY_BADGES_POLL_MS = 20_000

/**
 * Badges conquistadas pelo usuário autenticado (ordenadas por
 * earnedAt desc). Faz polling pra alimentar a notificação de
 * desbloqueio — a key é compartilhada, então o BadgesCard também
 * fica "ao vivo" sem request extra.
 *
 * `options.enabled` trava o polling quando não há sessão: o detector
 * (montado globalmente) passa `false` em páginas públicas — senão um
 * 401 em `/users/me/badges` dispararia o redirect pra `/auth` no
 * interceptor do `api`. Sem o parâmetro, fica habilitado (default do
 * React Query) — é o caso do `BadgesCard`, sempre atrás de rota
 * protegida.
 */
export function useMyBadges(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: MY_BADGES_KEY,
    queryFn: async () => {
      const response = await api.get<UserBadge[]>('/users/me/badges')
      return response.data
    },
    refetchInterval: MY_BADGES_POLL_MS,
    enabled: options?.enabled,
  })
}

/**
 * Catálogo de badges visíveis ao usuário (active=true + global OR sua
 * empresa). É o que o back já devolve em `GET /badges` desde a Sprint
 * Badges F1.
 */
export function useVisibleBadges() {
  return useQuery({
    queryKey: VISIBLE_BADGES_KEY,
    queryFn: async () => {
      const response = await api.get<Badge[]>('/badges')
      return response.data
    },
  })
}
